import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DataFormatSelect from './DataFormatSelect';
import DataTypeSelect from './DataTypeSelect';
import DeleteIcon from '@mui/icons-material/Delete';
import DraggablePaper from './DraggablePaper';
import FieldLabel from './FieldLabel';
import TagInput from './TagInput';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { DUPLICATE_SUCCESS_DELAY } from '../../constants';
import {
  DuplicateFieldsDialogProps,
  EditableField,
  SelectedField,
} from '../fields/utils/FieldTypes';
import { FIELD_DATA_TYPE_CONFIG_DEFAULT } from '../fields/FieldsSettings/FieldsSettingsTypes';
import { IField as IFieldMark } from '../../types/my-types';
import { IFieldReview } from '../review/form/FieldList';
import { MarkDispatchContext, MarkStateContext } from '../../context/MarkContext';
import { getConfigByDataType } from '../../utils/getConfigByDataType';
import { getDataTypeOptionsFromQuery } from '../../utils/getDataTypeOptionsFromQuery';
import { getDefaultDataFormat } from '../../utils/getDefaultDataFormat';
import { useFieldDuplicate } from '../hooks/useFieldDuplicate';
import { useQuery } from '@tanstack/react-query';
import { validateLabel } from '../../utils/validationUtils';

import DuplicateFieldsStyles from '../fields/utils/DuplicateFields.module.scss';

const DuplicateFieldsDialog: React.FC<DuplicateFieldsDialogProps> = ({
  extractionId,
  fieldLabels,
  isReview,
  trainingSetId,
}) => {
  const [editableFields, setEditableFields] = useState<{ [key: string]: EditableField }>({});
  const [hoveredFieldId, setHoveredFieldId] = useState<string | null>(null);

  const dispatch = useContext(MarkDispatchContext);
  const glynt = useGlynt();
  const { dataPoolId } = useContext(AppContext);
  const { selectedFields, selectedFieldsInfoMark, selectedFieldsInfoReview, showDuplicateModal } =
    useContext(MarkStateContext);

  const {
    duplicateFields,
    duplicationStatus,
    isAdding,
    isAddingReview,
    isDuplicatedDisabled,
    setDuplicationStatus,
    setIsDuplicatedDisabled,
  } = useFieldDuplicate({
    editableFields,
    isReview,
    extractionId,
    trainingSetId,
  });

  const selectedFieldsToUse = isReview ? selectedFieldsInfoReview : selectedFieldsInfoMark;

  const dtQuery = useQuery({
    queryKey: ['dataTypes'],
    queryFn: async () => await (await glynt).dataTypes.retrieve(),
    enabled: !!dataPoolId
  });
  const dataTypesResponse = dtQuery?.data;

  const handleClose = useCallback(() => {
    dispatch({ type: 'SET_DUPLICATE_MODAL', payload: false });
  }, [dispatch]);

  const onRemoveField = useCallback(
    (fieldId: string) => {
      if (isReview) {
        dispatch({
          type: 'SET_SELECTED_FIELDS',
          payload: selectedFields.filter((label) => label !== fieldId),
        });
        dispatch({
          type: 'REMOVE_SELECTED_FIELD_INFO',
          payload: fieldId,
          isReview: true,
        });
      } else {
        dispatch({
          type: 'SET_SELECTED_FIELDS',
          payload: selectedFields.filter((id) => id !== fieldId),
        });
        dispatch({
          type: 'REMOVE_SELECTED_FIELD_INFO',
          payload: fieldId,
        });
      }
    },
    [dispatch, selectedFields, isReview]
  );

  const getFieldKeyId = useCallback(
    (field: SelectedField, isReview: boolean = false) =>
      isReview ? (field as IFieldReview).label : (field as IFieldMark).id,
    []
  );

  // If there are no selected fields and the duplicate modal is open, close the modal and remove the fields
  useEffect(() => {
    if (
      Array.isArray(selectedFieldsToUse) &&
      selectedFieldsToUse.length === 0 &&
      showDuplicateModal
    ) {
      // Close the duplicate modal
      handleClose();
      // Remove the selected fields (which should be an empty array at this point)
      onRemoveField(selectedFieldsToUse.join(','));
    }
  }, [handleClose, onRemoveField, selectedFieldsToUse, showDuplicateModal]);

  // Initialize the state of editable fields when selectedFieldsInfoMark changes
  useEffect(() => {
    const mapSelectedFieldToEditable = (field: SelectedField): [string, EditableField] => {
      const fieldId = getFieldKeyId(field, isReview);

      return [
        fieldId,
        {
          content: isReview ? (field as IFieldReview)?.content || '' : '',
          data_type: field.data_type || 'string',
          data_type_config: field.data_type_config || {},
          error: false,
          glynt_tags: [...(field.glynt_tags || [])],
          label: `${field.label}-Copy`,
        },
      ];
    };

    if (Array.isArray(selectedFieldsToUse)) {
      const initialFields = selectedFieldsToUse
        .map(mapSelectedFieldToEditable)
        .reduce<{ [key: string]: EditableField }>((acc, [fieldId, editableField]) => {
          acc[fieldId] = editableField;
          return acc;
        }, {});

      setEditableFields(initialFields);
    } else {
      setEditableFields({});
    }
  }, [getFieldKeyId, isReview, selectedFieldsToUse, showDuplicateModal]);

  // If there are no selected fields, close the modal
  useEffect(() => {
    if (selectedFieldsToUse.length === 0 && showDuplicateModal) {
      handleClose();
      onRemoveField(selectedFieldsToUse.join(','));
    }
  }, [handleClose, onRemoveField, selectedFieldsToUse, showDuplicateModal]);

  // Verify if the label is valid and update the state of the button
  useEffect(() => {
    const hasError = Object.entries(editableFields).some(([fieldId, field]) => {
      if (duplicationStatus[fieldId]?.status === 'success') return false;
      const validationResult = validateLabel(field.label, fieldLabels);
      return !validationResult.isValid;
    });

    setIsDuplicatedDisabled(hasError);
  }, [duplicationStatus, editableFields, fieldLabels, setIsDuplicatedDisabled]);

  const handleDuplicationConfirmed = () => {
    dispatch({ type: 'FIELD_DUPLICATION_SUCCESS' });
  };

  // Clear the duplication status when the modal is closed
  const handleClearDuplicationStatus = () => {
    setDuplicationStatus({});
  };

  const handleFieldChange = (
    field: 'content' | 'data_type' | 'data_type_config' | 'glynt_tags' | 'label',
    fieldId: string,
    value: string | string[]
  ) => {
    setEditableFields((prevFields) => {
      const updatedFields = { ...prevFields };
      let updatedField = { ...updatedFields[fieldId] };

      // The following gets the default data format for the selected data type
      if (field === 'data_type') {
        updatedField = { ...updatedField, [field as string]: value };
        const defaultConfig =
          FIELD_DATA_TYPE_CONFIG_DEFAULT[value as keyof typeof FIELD_DATA_TYPE_CONFIG_DEFAULT];
        const dataTypeOptions = getDataTypeOptionsFromQuery(dataTypesResponse, value as string);
        const defaultDataFormat = getDefaultDataFormat(
          value as string,
          defaultConfig,
          dataTypeOptions
        );
        updatedField.data_type_config = defaultDataFormat;
      } else if (field === 'data_type_config') {
        let updatedConfig;
        const configValue = value as string;
        if (updatedField.data_type === 'amount' || updatedField.data_type === 'number') {
          updatedConfig = getConfigByDataType(updatedField.data_type, { decimalSymbol: configValue });
        } else {
          updatedConfig = getConfigByDataType(updatedField.data_type, { order: configValue });
        }
        updatedField.data_type_config = updatedConfig;
      } else {
        updatedField = { ...updatedField, [field]: value };
      }

      updatedFields[fieldId] = updatedField;
      return updatedFields;
    });
  };

  const handleDuplicate = async () => {
    const allSuccessful = await duplicateFields();
    if (allSuccessful) {
      setTimeout(() => {
        handleClose();
        handleDuplicationConfirmed();
      }, DUPLICATE_SUCCESS_DELAY);
    }
  };

  const isReadOnly = (fieldId: string) => duplicationStatus[fieldId]?.status === 'success';

  const selectedFieldsWithId = Array.isArray(selectedFieldsToUse)
    ? selectedFieldsToUse.map((field) => ({
        ...field,
        fieldId: getFieldKeyId(field, isReview),
      }))
    : [];

  return (
    <Dialog
      PaperComponent={DraggablePaper}
      PaperProps={{ id: 'fields-duplicate-modal' }}
      TransitionProps={{ onExited: handleClearDuplicationStatus }}
      aria-labelledby="selected-fields-dialog-title"
      classes={{ paper: DuplicateFieldsStyles.paper }}
      data-cy="fields-duplicate-modal"
      fullWidth
      maxWidth={false}
      onClose={handleClose}
      open={showDuplicateModal}
    >
      <DialogTitle
        className={DuplicateFieldsStyles.dialogTitle}
        data-cy="draggable-dialog-title"
        id="draggable-dialog-title" // id should be the same as the Dragable component
      >
        Duplicate Fields
      </DialogTitle>

      <DialogContent>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Label</TableCell>
              <TableCell>Data Type</TableCell>
              <TableCell>Data Format</TableCell>
              <TableCell>Glynt Tags</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedFieldsWithId.map((field) => (
              <React.Fragment key={field.fieldId}>
                <TableRow
                  data-cy={`duplicate-modal-field-row-${field.label}`}
                  hover
                  key={field.fieldId}
                  onMouseEnter={() => setHoveredFieldId(field.fieldId)}
                  onMouseLeave={() => setHoveredFieldId(null)}
                  style={{ position: 'relative' }}
                >
                  <TableCell>
                    <IconButton
                      onClick={() => onRemoveField(field.fieldId)}
                      className={`${DuplicateFieldsStyles.iconButton} ${
                        hoveredFieldId === field.fieldId
                          ? DuplicateFieldsStyles.iconButtonVisible
                          : ''
                      } ${
                        duplicationStatus[field.fieldId]?.status !== 'success'
                          ? DuplicateFieldsStyles.iconButtonVisible
                          : ''
                      }`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <FieldLabel
                      fieldLabels={fieldLabels}
                      hasApiError={duplicationStatus[field.fieldId]?.status === 'error'}
                      initialLabel={editableFields[field.fieldId]?.label || ''}
                      onLabelChange={(newLabel) =>
                        handleFieldChange('label', field.fieldId, newLabel)
                      }
                      originalLabel={field.label}
                      readOnly={isReadOnly(field.fieldId)}
                      setIsDuplicatedDisabled={setIsDuplicatedDisabled}
                    />
                  </TableCell>
                  <TableCell>
                    <DataTypeSelect
                      dataTypesResponse={dataTypesResponse}
                      handleDataTypeChange={(newValue) =>
                        handleFieldChange('data_type', field.fieldId, newValue)
                      }
                      haveTitle={false}
                      readOnly={isReadOnly(field.fieldId)}
                      selectedDataType={editableFields[field.fieldId]?.data_type || ''}
                    />
                  </TableCell>
                  <TableCell>
                    {editableFields[field.fieldId]?.data_type !== 'string' && (
                      <DataFormatSelect
                        dataFormat={editableFields[field.fieldId]?.data_type_config}
                        dataType={editableFields[field.fieldId]?.data_type}
                        dataTypesResponse={dataTypesResponse}
                        fieldId={field.fieldId}
                        handleDataFormatChange={(e) =>
                          handleFieldChange(
                            'data_type_config',
                            field.fieldId,
                            e.target.value as string
                          )
                        }
                        haveTitle={false}
                        readOnly={isReadOnly(field.fieldId)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <TagInput
                      cypressId={`${field.label}-new-glynt-tag`}
                      readOnly={isReadOnly(field.fieldId)}
                      setTags={(newTags) => handleFieldChange('glynt_tags', field.fieldId, newTags)}
                      tags={editableFields[field.fieldId]?.glynt_tags || []}
                    />
                  </TableCell>
                  <TableCell style={{ paddingLeft: '0', paddingRight: '2rem' }}>
                    <div style={{ display: 'flex' }}>
                      <Tooltip title="Successfully Duplicated">
                        <CheckIcon
                          className={`${DuplicateFieldsStyles.successIcon} ${
                            isReadOnly(field.fieldId)
                              ? DuplicateFieldsStyles.successIconVisible
                              : ''
                          }`}
                          color="primary"
                          data-cy="icon-duplicate-success"
                        />
                      </Tooltip>
                      <Tooltip title="Duplication Failed">
                        <CloseIcon
                          color="error"
                          className={`${DuplicateFieldsStyles.errorIcon} ${
                            duplicationStatus[field.fieldId]?.status === 'error'
                              ? DuplicateFieldsStyles.errorIconVisible
                              : ''
                          }`}
                          data-cy="icon-duplicate-error"
                        />
                      </Tooltip>
                    </div>
                  </TableCell>
                  {duplicationStatus[field.fieldId]?.status === 'error' && (
                    <span className={DuplicateFieldsStyles.errorMessage}>
                      Error: {duplicationStatus[field.fieldId]?.message || 'An error ocurred.'}
                    </span>
                  )}
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button color="primary" data-cy="btn-cancel-duplicate" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className={DuplicateFieldsStyles.duplicateButton}
          color="primary"
          data-cy="btn-duplicate-confirm"
          disabled={isDuplicatedDisabled || isAdding || isAddingReview}
          onClick={handleDuplicate}
        >
          {isAdding || isAddingReview ? <CircularProgress size={25} /> : 'Duplicate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateFieldsDialog;
